import * as React from 'react';
import { QRCodeSVG } from 'qrcode.react';
import {
  AccountPortalShell,
  HCard,
  HPrimaryButton,
  HSecondaryButton,
  HGhostButton,
  HButtonPair,
  SerifHeading,
  SectionLabel,
  FieldLabel,
} from '../h';

/* GuestDeviceSetupView — FEAT-133 v9 §7.1 – 7.3.

   Fresh rewrite of the guest Hearth-setup flow. Renders three stages via a
   state machine inside the AccountPortalShell (the guest-flow chrome with the
   higlo logo + "Account Portal" caps label, no back button).

   Stages:
     - connect (7.1) — Connect Hearth to TV, numbered instructions, single
       full-width Continue.
     - wifi    (7.2) — SSID + password entry. Back/Continue + "already on
       WiFi — skip" ghost link.
     - qr      (7.3) — QR display. No 2-hour validity line (guest tokens are
       ephemeral). Single full-width Back (no "I'm done" CTA).

   Props: { familyName, initialStage, onCancel }
     familyName    — interpolated family name (e.g. "The Smith family").
     initialStage  — "connect" | "wifi" | "qr" — lets previews hop in.
     onCancel      — optional; currently unused because the guest flow has no
                     back-out path. Kept in the prop surface for parity with
                     the owner HearthSetupView.
*/

/* ────────── Shared bits ────────── */

/* 3-bar progress strip. Caption reads e.g. "Step 2 of 3 · Connect to TV …". */
function ProgressStrip({ step, caption }) {
  return (
    <div className="mb-4">
      <div className="flex items-center gap-1.5">
        {[0, 1, 2].map((idx) => (
          <div
            key={idx}
            className={
              'flex-1 h-1 rounded-full ' +
              (idx < step ? 'bg-slate-900' : 'bg-slate-200')
            }
          />
        ))}
      </div>
      <div className="text-[11px] text-slate-400 mt-1.5">{caption}</div>
    </div>
  );
}

const iconBase = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
};

function WifiIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" className={className} {...iconBase}>
      <path d="M5 12.55a11 11 0 0 1 14.08 0" />
      <path d="M1.42 9a16 16 0 0 1 21.16 0" />
      <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
      <line x1="12" y1="20" x2="12.01" y2="20" />
    </svg>
  );
}

function EyeIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" className={className} {...iconBase}>
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function InfoIcon({ className, style }) {
  return (
    <svg viewBox="0 0 24 24" className={className} style={style} {...iconBase}>
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="16" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
  );
}

/* Extract the surname from a "family name" like "The Smith family".
   We strip the leading "The " and trailing " family", leaving "Smith".
   Used for the 7.3 info-card copy that addresses the owner by first name. */
function extractOwnerName(familyName) {
  if (!familyName) return '';
  let s = String(familyName).trim();
  s = s.replace(/^the\s+/i, '');
  s = s.replace(/\s+family$/i, '');
  s = s.trim();
  if (!s) return String(familyName);
  // If there's still a multi-word thing, take the first word.
  return s.split(/\s+/)[0];
}

/* ────────── Stage 7.1 — Connect to TV ────────── */

function ConnectStage({ familyName, onContinue }) {
  return (
    <AccountPortalShell>
      <div className="px-4 pt-4 pb-6">
        <SectionLabel className="mb-2">Setting up for</SectionLabel>
        <SerifHeading size="lg" className="mb-2">
          {familyName}
          {'\u2019'}s extra Hearth
        </SerifHeading>
        <p className="text-[13px] text-slate-500 leading-relaxed mb-4">
          You were sent this link so you can set up the Hearth that was posted
          to you. Two short steps.
        </p>

        <ProgressStrip
          step={1}
          caption={`Step 1 of 3 \u00B7 Connect to TV \u2192 WiFi \u2192 Scan QR`}
        />

        <HCard variant="muted" className="p-4 mb-4">
          <div className="text-[12px] text-slate-600 leading-relaxed">
            Get the Hearth out of the box, plug it into your TV, and turn it
            on.
          </div>
        </HCard>

        <SectionLabel className="mb-2">Then check the TV</SectionLabel>
        <ol className="text-[13px] text-slate-700 space-y-2 leading-relaxed list-decimal ml-5 mb-6">
          <li>
            You should see a {'\u201C'}Setup Device{'\u201D'} screen with a
            camera icon.
          </li>
          <li>
            If you see something else, give it a minute {'\u2014'} the Hearth
            needs to boot up.
          </li>
          <li>
            Once you see {'\u201C'}Setup Device{'\u201D'}, tap Continue.
          </li>
        </ol>

        <HPrimaryButton
          type="button"
          onClick={onContinue}
          className="w-full"
        >
          Continue
        </HPrimaryButton>
      </div>
    </AccountPortalShell>
  );
}

/* ────────── Stage 7.2 — WiFi ────────── */

function WifiStage({ familyName, ssid, password, onSsidChange, onPasswordChange, onBack, onContinue, onSkip }) {
  const [showPw, setShowPw] = React.useState(false);
  const [error, setError] = React.useState('');

  const handleContinue = (e) => {
    e.preventDefault();
    // Match OLD GuestDeviceSetup validation: SSID non-empty and doesn't
    // contain a semicolon (our QR payload is semicolon-delimited).
    if (!ssid.trim()) {
      setError('Enter your WiFi network name, or tap skip below.');
      return;
    }
    if (ssid.includes(';')) {
      setError('WiFi network names with a semicolon aren\u2019t supported. Rename the network or use a different one.');
      return;
    }
    setError('');
    onContinue();
  };

  return (
    <AccountPortalShell>
      <form className="px-4 pt-4 pb-6" onSubmit={handleContinue}>
        <SectionLabel className="mb-2">
          Setting up {familyName}
          {'\u2019'}s extra Hearth
        </SectionLabel>
        <SerifHeading size="lg" className="mb-2">
          What{'\u2019'}s your home WiFi?
        </SerifHeading>
        <p className="text-[13px] text-slate-600 leading-relaxed mb-4">
          The Hearth uses this to get online. If it{'\u2019'}s already
          connected, you can skip.
        </p>

        <ProgressStrip
          step={2}
          caption={`Step 2 of 3 \u00B7 Connect to TV \u2192 WiFi \u2192 Scan QR`}
        />

        <FieldLabel htmlFor="gds-ssid" className="mb-1.5">
          Network name (SSID)
        </FieldLabel>
        <HCard className="px-3 py-2.5 flex items-center gap-2 mb-3">
          <WifiIcon className="w-4 h-4 text-slate-500 flex-shrink-0" />
          <input
            id="gds-ssid"
            className="flex-1 text-[14px] outline-none bg-transparent text-slate-900 placeholder:text-slate-400"
            value={ssid}
            onChange={(e) => onSsidChange(e.target.value)}
            placeholder="Your WiFi network name"
            autoComplete="off"
          />
        </HCard>
        <p className="text-[11px] text-slate-400 mb-4 leading-relaxed">
          Type it exactly as it appears on your router {'\u2014'} case and
          spaces matter.
        </p>

        <FieldLabel htmlFor="gds-pw" className="mb-1.5">
          Password
        </FieldLabel>
        <HCard className="px-3 py-2.5 flex items-center gap-2 mb-6">
          <input
            id="gds-pw"
            type={showPw ? 'text' : 'password'}
            className="flex-1 text-[14px] outline-none bg-transparent text-slate-900 placeholder:text-slate-400"
            value={password}
            onChange={(e) => onPasswordChange(e.target.value)}
            placeholder="WiFi password"
            autoComplete="off"
          />
          <button
            type="button"
            onClick={() => setShowPw((v) => !v)}
            className="text-slate-500 hover:text-slate-700 flex-shrink-0"
            aria-label={showPw ? 'Hide password' : 'Show password'}
          >
            <EyeIcon className="w-4 h-4" />
          </button>
        </HCard>

        {error && (
          <p className="text-[12px] text-red-600 mb-3 leading-relaxed">{error}</p>
        )}

        <HButtonPair className="mb-2">
          <HSecondaryButton type="button" onClick={onBack}>
            Back
          </HSecondaryButton>
          <HPrimaryButton type="submit">Continue</HPrimaryButton>
        </HButtonPair>
        <HGhostButton
          type="button"
          onClick={onSkip}
          className="w-full text-[13px]"
        >
          The Hearth is already on WiFi {'\u2014'} skip
        </HGhostButton>
      </form>
    </AccountPortalShell>
  );
}

/* ────────── Stage 7.3 — QR display ────────── */

/* QR payload — IDENTICAL to the OLD GuestDeviceSetup.renderStep3 array form:
     [ `${ssid};`, `${password};`, `${familyID}@family.higlo.ai;`, `${token};` ]
   The TVApp splits the decoded text on `;` and reads four fields in order
   (ssid, password, familyUserID, token). Empty WiFi fields are intentional
   when the user picks "already on WiFi" — they decode to empty strings. */
function buildQrPayload({ ssid, password, familyID, token }) {
  const safeSsid = ssid || '';
  const safePw = password || '';
  const fid = familyID || '';
  const tok = token || '';
  return [
    `${safeSsid};`,
    `${safePw};`,
    `${fid}@family.higlo.ai;`,
    `${tok};`,
  ];
}

function QrStage({ familyName, familyID, token, ssid, password, onBack }) {
  const ownerName = extractOwnerName(familyName);
  const qrValue = buildQrPayload({ ssid, password, familyID, token });
  const hasWifi = !!ssid;
  return (
    <AccountPortalShell>
      <div className="px-4 pt-4 pb-6">
        <SectionLabel className="mb-2">
          Setting up {familyName}
          {'\u2019'}s extra Hearth
        </SectionLabel>
        <SerifHeading size="md" className="mb-2">
          Show this QR to the Hearth
        </SerifHeading>
        <p className="text-[13px] text-slate-600 leading-relaxed mb-4">
          {hasWifi
            ? `Hold your phone up to the Hearth\u2019s camera. It\u2019ll connect to your WiFi and finish setting up.`
            : `Hold your phone up to the Hearth\u2019s camera. WiFi setup was skipped \u2014 the Hearth will use its existing network.`}
        </p>

        <ProgressStrip
          step={3}
          caption={`Step 3 of 3 \u00B7 Done after scanning`}
        />

        <HCard className="p-4 flex justify-center mb-4" style={{ background: '#fff' }}>
          <QRCodeSVG
            value={qrValue}
            size={280}
          />
        </HCard>

        <HCard variant="next" className="p-3 mb-4 flex items-start gap-2">
          <InfoIcon
            className="w-4 h-4 flex-shrink-0 mt-0.5"
            style={{ color: '#1e40af' }}
          />
          <div className="text-[12px] text-slate-700 leading-relaxed">
            Once the Hearth scans this, it{'\u2019'}ll appear ready in
            {ownerName ? ` ${ownerName}\u2019s` : ' the owner\u2019s'} Account
            Portal.
          </div>
        </HCard>

        <HSecondaryButton
          type="button"
          onClick={onBack}
          className="w-full"
        >
          Back
        </HSecondaryButton>
        <p className="text-[11px] text-slate-400 mt-3 text-center">
          Tap Back to fix WiFi if the Hearth can{'\u2019'}t connect.
        </p>
      </div>
    </AccountPortalShell>
  );
}

/* ────────── Link decode + error / loading states ────────── */

/* Decode the `?data=` base64 blob the owner-side HearthSetupView generates.
   Payload shape (matches OLD DeviceSetup.generateGuestLink):
     { token, fID, fName, ts, pad }
   Returns { familyName, familyID, token } on success, or throws a string
   error message on failure. */
function decodeGuestLink() {
  const urlParams = new URLSearchParams(window.location.search);
  const encoded = urlParams.get('data');
  if (!encoded) {
    throw 'The link you\u2019ve entered is incorrect or incomplete. Check the link you received or ask your contact to send you a new one.';
  }
  let decoded;
  try {
    decoded = JSON.parse(atob(encoded));
  } catch (_) {
    throw 'This link is invalid or corrupted. Ask your contact to send you a new one.';
  }
  const { token, fID, fName, ts } = decoded || {};
  if (!token || !fID || !fName || !ts) {
    throw 'The link you\u2019ve entered is incorrect or incomplete. Check the link you received or ask your contact to send you a new one.';
  }
  const nowSec = Math.floor(Date.now() / 1000);
  const oneWeekSec = 7 * 24 * 60 * 60;
  if (nowSec - parseInt(ts, 10) > oneWeekSec) {
    throw 'This link is only valid for 1 week and has expired. Ask the account holder to send you a new one.';
  }
  return { familyName: fName, familyID: fID, token };
}

function ErrorStage({ message }) {
  return (
    <AccountPortalShell>
      <div className="px-4 pt-4 pb-6">
        <SerifHeading size="md" className="mb-2">
          Hmm, this link doesn{'\u2019'}t look right
        </SerifHeading>
        <HCard variant="warn" className="p-4 mt-2">
          <p className="text-[13px] text-slate-700 leading-relaxed">{message}</p>
        </HCard>
      </div>
    </AccountPortalShell>
  );
}

function LoadingStage() {
  return (
    <AccountPortalShell>
      <div className="px-4 pt-8 pb-6 text-center">
        <p className="text-[13px] text-slate-500">Checking your link\u2026</p>
      </div>
    </AccountPortalShell>
  );
}

/* ────────── GuestDeviceSetupView — top-level state machine ──────────

   Renders the guest 3-stage flow. In production it reads the
   `?data=<base64>` URL param generated by the owner-side HearthSetupView
   (OLD: DeviceSetup.generateGuestLink) to recover { familyName, familyID,
   token }, validates / expiry-checks it, and passes real QR content
   through to the QR stage.

   Preview mode: pass `familyName` (and optionally `familyID` / `passcode`)
   to bypass URL decoding. That's what the mockup previews use — they
   don't have a real `?data=` query string. */
export function GuestDeviceSetupView({
  // Preview-only overrides
  familyName: familyNameProp,
  familyID: familyIDProp,
  passcode: passcodeProp,
  initialStage = 'connect',
  // Kept in the prop surface for parity with HearthSetupView; the guest
  // flow has no in-UI cancel affordance today.
  // eslint-disable-next-line no-unused-vars
  onCancel,
}) {
  const isPreview = Boolean(familyNameProp);

  // linkData === null → still resolving (no preview, no URL yet consumed).
  // linkData === undefined after mount effect finishes with no hit.
  const [linkData, setLinkData] = React.useState(() => {
    if (isPreview) {
      return {
        familyName: familyNameProp,
        familyID: familyIDProp || 'preview-family-id',
        token: passcodeProp || 'preview-token',
      };
    }
    return null;
  });
  const [linkError, setLinkError] = React.useState(null);

  React.useEffect(() => {
    if (isPreview) return;
    try {
      setLinkData(decodeGuestLink());
    } catch (msg) {
      setLinkError(typeof msg === 'string' ? msg : 'This link couldn\u2019t be read. Ask your contact to send you a new one.');
    }
  }, [isPreview]);

  // Shared WiFi credentials — WifiStage edits them, QrStage reads them into
  // the QR payload. Lifted here so back-nav doesn't wipe the values.
  const [wifiSSID, setWifiSSID] = React.useState('');
  const [wifiPassword, setWifiPassword] = React.useState('');

  const [stage, setStage] = React.useState(initialStage);

  if (linkError) return <ErrorStage message={linkError} />;
  if (!linkData) return <LoadingStage />;

  const { familyName, familyID, token } = linkData;

  switch (stage) {
    case 'wifi':
      return (
        <WifiStage
          familyName={familyName}
          ssid={wifiSSID}
          password={wifiPassword}
          onSsidChange={setWifiSSID}
          onPasswordChange={setWifiPassword}
          onBack={() => setStage('connect')}
          onContinue={() => setStage('qr')}
          onSkip={() => {
            // "Already on WiFi" → empty payload tells the Hearth not to
            // reconfigure its network (matches OLD handleSkipWiFi).
            setWifiSSID('');
            setWifiPassword('');
            setStage('qr');
          }}
        />
      );
    case 'qr':
      return (
        <QrStage
          familyName={familyName}
          familyID={familyID}
          token={token}
          ssid={wifiSSID}
          password={wifiPassword}
          onBack={() => setStage('wifi')}
        />
      );
    case 'connect':
    default:
      return (
        <ConnectStage
          familyName={familyName}
          onContinue={() => setStage('wifi')}
        />
      );
  }
}

export default GuestDeviceSetupView;
